import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Building2, Award, ArrowLeft, DollarSign } from 'lucide-react'
import { useApi } from '../../shared/hooks/useApi'

interface OrganizationContractor {
  contractor_name: string
  contractor_uuid: string
  contractor_national_id?: string
  wins_count: number
  total_value?: number
  cpv_groups?: string[]
}

const OrganizationDetailPage = () => {
  const { organizationName: encodedOrgName } = useParams<{ organizationName: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Decode organization name from URL
  const organizationName = decodeURIComponent(encodedOrgName || '')

  // Get filters from URL parameters (year is optional, only filter if explicitly provided)
  const yearParam = searchParams.get('year')
  const year = yearParam ? parseInt(yearParam) : undefined
  const cpvFilter = searchParams.get('cpv') || ''
  const dateFrom = searchParams.get('date_from') || ''
  const dateTo = searchParams.get('date_to') || ''

  const { getOrganizationContractors } = useApi()

  const [data, setData] = useState<OrganizationContractor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        const result = await getOrganizationContractors(
          organizationName,
          cpvFilter,
          dateFrom,
          dateTo,
          year,
        )

        setData(result)
      } catch (err) {
        console.error('Failed to load organization contractors:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    if (organizationName) {
      loadData()
    }
  }, [organizationName, cpvFilter, dateFrom, dateTo, year, getOrganizationContractors])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
        <p className="text-danger">Błąd podczas ładowania danych wykonawców: {error}</p>
      </div>
    )
  }

  const totalWins = data?.reduce((sum, item) => sum + item.wins_count, 0) || 0
  const totalValue = data?.reduce((sum, item) => sum + (item.total_value || 0), 0) || 0

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-ink-secondary hover:text-ink"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Powrót do organizacji</span>
            </button>
            <div className="border-l border-border h-8"></div>
            <div>
              <h2 className="text-xl font-semibold text-ink flex items-center">
                <Building2 className="h-6 w-6 mr-2 text-brand" />
                {organizationName}
              </h2>
              <p className="text-sm text-ink-secondary mt-1">
                Wykonawcy, którzy wygrali postępowania tego Zamawiającego
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-brand-bg rounded-lg">
              <Award className="h-6 w-6 text-brand" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Razem zwycięstw</p>
              <p className="text-2xl font-semibold text-ink">
                {totalWins.toLocaleString('pl-PL')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-success/10 rounded-lg">
              <DollarSign className="h-6 w-6 text-success" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Wartość całkowita</p>
              <p className="text-xl font-semibold text-ink">
                {new Intl.NumberFormat('pl-PL', {
                  style: 'currency',
                  currency: 'PLN',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(totalValue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <div className="flex items-center">
            <div className="p-2 bg-ai/10 rounded-lg">
              <Award className="h-6 w-6 text-ai" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-secondary">Wykonawców</p>
              <p className="text-2xl font-semibold text-ink">{data?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contractors Table */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-medium text-ink">
            Lista Wykonawców, którzy wygrali postępowania u tego Zamawiającego
          </h3>
          <p className="text-sm text-ink-secondary mt-1">
            Kliknij w nazwę wykonawcy, aby zobaczyć wszystkie jego kontrakty
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Wykonawca
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Zwycięstw
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Wartość całkowita
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Kategorie CPV
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-raised divide-y divide-border">
              {data?.map((item, index) => (
                <tr key={index} className="hover:bg-surface-hover">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() =>
                        navigate(
                          `/contractors/relationship/${encodeURIComponent(organizationName)}/${item.contractor_uuid}?contractorName=${encodeURIComponent(item.contractor_name)}`,
                        )
                      }
                      className="text-sm font-medium text-success hover:text-success hover:underline"
                    >
                      {item.contractor_name}
                    </button>
                    {item.contractor_national_id && (
                      <div className="text-xs text-ink-muted mt-1">
                        NIP: {item.contractor_national_id}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink">
                    {item.wins_count.toLocaleString('pl-PL')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink">
                    {item.total_value
                      ? new Intl.NumberFormat('pl-PL', {
                          style: 'currency',
                          currency: 'PLN',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(item.total_value)
                      : 'Brak danych'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {item.cpv_groups?.slice(0, 3).map((cpv, cpvIndex) => (
                        <button
                          key={cpvIndex}
                          onClick={() => {
                            const params = new URLSearchParams(searchParams)
                            params.set('cpv', cpv)
                            navigate(`/organizations?${params.toString()}`)
                          }}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ai/10 text-ai hover:bg-ai/10"
                        >
                          {cpv}
                        </button>
                      ))}
                      {item.cpv_groups && item.cpv_groups.length > 3 && (
                        <span className="text-xs text-ink-muted">
                          +{item.cpv_groups.length - 3} więcej
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!data || data.length === 0) && (
          <div className="px-6 py-8 text-center text-ink-muted">
            <Award className="h-12 w-12 mx-auto mb-4 text-ink-faint" />
            <p>Brak wykonawców dla tej organizacji w wybranym okresie</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrganizationDetailPage
